import { useState } from "react";
import { Button } from "../ui/Button";
import { Field, FieldNote, Form, NumInput } from "../ui/Field";
import { Modal } from "../ui/Overlay";
import { Notice } from "../ui/Notice";
import { I18nField, missingLocales, type I18nText } from "./I18nField";
import { fmt, type Level } from "../../lib/model";
import type { Money } from "../../lib/big";
import { useT } from "../../lib/i18n";

/**
 * 新增关卡。关卡数量由后台配置，**没有固定上限**，也可以插在中间。
 *
 * 插在中间意味着其后所有关的序号会前移一位——所以这里要把影响面直说，
 * 而不是插完了让策划自己发现「第 12 关怎么变成第 13 关了」。
 */
export function AddLevelDialog({
  levels, defaultAt, onClose, onSubmit, curveTarget,
}: {
  levels: Level[];
  defaultAt: number;
  curveTarget?: (at: number) => bigint | null;
  onClose: () => void;
  onSubmit: (v: { at: number; name: string; i18n: I18nText; targetRolls: Money | null }) => void;
}) {
  const [at, setAt] = useState(defaultAt);
  const [i18n, setI18n] = useState<I18nText>({});
  const [rolls, setRolls] = useState<Money | null>(null);
  const [tried, setTried] = useState(false);
  const t = useT();

  const missing = missingLocales(i18n).includes("en");
  const shifted = levels.length - at + 1;
  const prev = levels[at - 2];

  return (
    <Modal
      title={t("dlg.addLevel")}
      width={560}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            tone="success"
            onClick={() => {
              if (missing) return setTried(true);
              onSubmit({ at, name: (i18n.en ?? "").trim(), i18n, targetRolls: rolls });
            }}
          >
            {t("common.add")}
          </Button>
        </>
      }
    >
      <Form>
        <Field label={t("dlg.insertAt")}>
          <NumInput
            value={at}
            className="w-[72px]"
            onChange={(v) => setAt(Math.min(Math.max(Number(v ?? 1), 1), levels.length + 1))}
          />
          <span className="text-xs text-ink-placeholder">{t("dlg.nowTotal", { n: levels.length })}</span>
        </Field>

        {/* 影响面警告落在控件列：它说的是上面那个序号，不是整个弹窗 */}
        {shifted > 0 && (
          <FieldNote><Notice tone="warning">{t("dlg.shiftWarn", { at, n: shifted })}</Notice></FieldNote>
        )}

        <Field
          label={t("dlg.cityName")}
          required
          hint={tried && missing ? <span className="text-danger">{t("dlg.enRequired")}</span> : undefined}
        >
          <I18nField value={i18n} onChange={setI18n} width={320} />
        </Field>

        <Field label={t("dlg.targetRolls")}>
          <NumInput exact value={rolls} className="w-[120px]" onChange={setRolls} placeholder={curveTarget ? fmt(curveTarget(at)) : t("dlg.rollsDefault")} title={curveTarget ? t("cost.rollsHint") : undefined} />
          <span className="text-xs text-ink-placeholder">{prev ? t("dlg.prevLevel", { v: fmt(curveTarget ? prev.targetRollsOverride ?? curveTarget(prev.id) : prev.targetRolls) }) : t("dlg.firstLevel")}</span>
        </Field>
        <FieldNote full><Notice tone="plain">{t("dlg.costNote")}</Notice></FieldNote>

        <FieldNote full><Notice tone="plain">{t("dlg.blankNote")}</Notice></FieldNote>
      </Form>
    </Modal>
  );
}
